import DifferenceIcon from "@mui/icons-material/Difference"
import { Stack, Typography } from "@mui/material"
import { ReactElement } from "react"

import type { TAmendmentHistoryCardProps } from "./amendmentHistoryTypes"
import DifferencesDisplay from "./differencesDisplay"
import EscalatedOutcomesList from "./escalatedOutcomesList"
import { GRID_SPACING } from "../../../themes/defaultThemeParams"
import {
    AppAccordion,
    AppAccordionDetails,
    AppAccordionSummary,
} from "../../dataDisplay/accordions/appAccordions"
import { AppCard, AppCardContent, AppCardHeader } from "../../surfaces/appCard"

/**
 * Displays the amendment history for a DNO application as a card
 * with chronologically-ordered accordions.  Each accordion shows the
 * ENA assessment escalated outcomes and a table of field-level
 * differences for that amendment version.
 *
 * The list is displayed in reverse order (latest amendment first).
 * The first entry is always labelled "Original Application".
 */
const AmendmentHistoryCard = ({
    amendmentHistory,
    applicationId,
    isRedacted,
}: TAmendmentHistoryCardProps): ReactElement => {
    const hasHistory = amendmentHistory && amendmentHistory.length > 0

    const orderedHistory = hasHistory
        ? amendmentHistory
              .map((entry, originalIndex) => ({
                  entry,
                  originalIndex,
              }))
              .reverse()
        : []

    return (
        <AppCard>
            <AppCardHeader
                avatar={<DifferenceIcon fontSize="large" />}
                title="Amendment History"
                subheader="Changes and escalated outcomes by application version"
                titleTypographyProps={{
                    variant: "h5",
                    fontWeight: "500",
                }}
            />
            <AppCardContent>
                {hasHistory ? (
                    <Stack spacing={GRID_SPACING}>
                        {orderedHistory.map(({ entry, originalIndex }) => {
                            const title =
                                originalIndex === 0
                                    ? `Original Application - ${entry.applicationTimeStamp}`
                                    : `Amendment ${originalIndex} - ${entry.applicationTimeStamp}`

                            return (
                                <AppAccordion
                                    key={`amendment-${originalIndex}`}
                                >
                                    <AppAccordionSummary>
                                        <Typography fontWeight="500">
                                            {title}
                                        </Typography>
                                    </AppAccordionSummary>
                                    <AppAccordionDetails>
                                        <Stack spacing={2}>
                                            <Stack spacing={1}>
                                                <Typography
                                                    variant="h6"
                                                    fontWeight="500"
                                                >
                                                    Escalated Outcomes
                                                </Typography>
                                                <EscalatedOutcomesList
                                                    outcomes={
                                                        entry.escalatedOutcomes ||
                                                        {}
                                                    }
                                                />
                                            </Stack>
                                            <DifferencesDisplay
                                                differences={entry.differences}
                                                applicationId={applicationId}
                                                isRedacted={isRedacted}
                                            />
                                        </Stack>
                                    </AppAccordionDetails>
                                </AppAccordion>
                            )
                        })}
                    </Stack>
                ) : (
                    <Typography color="text.secondary">
                        Amendment history is unavailable for applications last
                        submitted before 11 May 2026. Any future amendments will
                        be shown here.
                    </Typography>
                )}
            </AppCardContent>
        </AppCard>
    )
}

export default AmendmentHistoryCard
